import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Layers, Mail, PackageCheck, RefreshCw } from 'lucide-react';
import { fetchAllOpenPurchaseOrders, type OpenPurchaseOrder } from '@/lib/api';
import {
  assignPurchaseOrderToRound,
  createOrderRound,
  lockOrderRound,
  updatePurchaseOrderItemQuantities,
} from '@/lib/api/rounds';
import {
  fetchFreeStockByProduct,
  reserveProductStock,
  type FreeStockLot,
} from '@/lib/api/inventory';
import {
  bundleBySupplierProduct,
  reduceOrderQty,
  type ProductTotal,
  type PurchaseLine,
} from '@/lib/orderRounds';
import { StockUsePicker } from '@/components/StockUsePicker';
import { formatEur } from '@/lib/pdf-brand';
import type { GiftSupplier } from '@/lib/types';

interface SupplierGroup {
  supplier: GiftSupplier;
  orders: OpenPurchaseOrder[];
  products: ProductTotal[];
  total: number;
}

export function PurchaseOverviewPage() {
  const [orders, setOrders] = useState<OpenPurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [placing, setPlacing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [stockMap, setStockMap] = useState<Map<string, FreeStockLot[]>>(new Map());
  const [reserving, setReserving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setBusy(true);
    try {
      const open = await fetchAllOpenPurchaseOrders();
      setOrders(open);
      const productIds = [
        ...new Set(
          open
            .filter((o) => !o.order_round_id)
            .flatMap((o) => (o.items ?? []).map((it) => it.gift_product_id))
        ),
      ];
      setStockMap(await fetchFreeStockByProduct(productIds));
    } finally {
      setBusy(false);
      setLoading(false);
    }
  }

  async function reserveStock(productId: string, qty: number) {
    setReserving(productId);
    setError(null);
    try {
      // 1. Reserveer de gekozen voorraad (FR-7.7).
      await reserveProductStock(stockMap.get(productId) ?? [], qty);
      // 2. Laat de besteleenheid dalen: verlaag de netto-behoefte over de niet-geplaatste
      //    PO-regels van dit product (FIFO), opnieuw op VPE afgerond.
      let remaining = qty;
      for (const o of orders) {
        if (o.order_round_id || remaining <= 0) continue;
        for (const it of o.items ?? []) {
          if (remaining <= 0) break;
          if (it.gift_product_id !== productId) continue;
          const r = reduceOrderQty(
            {
              totalUnitsOrdered: it.total_units_ordered,
              leftoverUnits: it.leftover_units ?? 0,
              vpe: it.gift_product?.vpe ?? 0,
            },
            remaining
          );
          if (r.consumed > 0) {
            await updatePurchaseOrderItemQuantities(it.id, {
              total_units_ordered: r.totalUnitsOrdered,
              vpe_units_ordered: r.vpeUnitsOrdered,
              leftover_units: r.leftoverUnits,
            });
            remaining -= r.consumed;
          }
        }
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reserveren mislukt');
    } finally {
      setReserving(null);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  // Alleen nog niet-geplaatste inkoop (geen ronde) is "te bestellen".
  const groups = useMemo<SupplierGroup[]>(() => {
    const map = new Map<string, SupplierGroup>();
    for (const o of orders) {
      if (o.order_round_id) continue;
      const s = o.supplier;
      if (!s) continue;
      const entry = map.get(s.id) ?? { supplier: s, orders: [], products: [], total: 0 };
      entry.orders.push(o);
      entry.total += (o.items ?? []).reduce(
        (sum, it) =>
          sum + (it.actual_invoice_price ?? it.quoted_unit_price) * it.total_units_ordered,
        0
      );
      map.set(s.id, entry);
    }
    for (const entry of map.values()) {
      const lines: PurchaseLine[] = [];
      for (const o of entry.orders) {
        for (const it of o.items ?? []) {
          lines.push({
            productId: it.gift_product_id,
            productName: it.gift_product?.name ?? 'Onbekend product',
            clientName: null,
            projectName: o.project?.name ?? 'Onbekend project',
            projectId: o.package?.project_id ?? '',
            quantity: it.total_units_ordered,
          });
        }
      }
      entry.products = bundleBySupplierProduct(lines);
    }
    return [...map.values()];
  }, [orders]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function placeRound(group: SupplierGroup) {
    setPlacing(group.supplier.id);
    setError(null);
    try {
      const round = await createOrderRound(group.supplier.id);
      for (const o of group.orders) {
        await assignPurchaseOrderToRound(o.id, round.id);
      }
      await lockOrderRound(round.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ronde plaatsen mislukt');
    } finally {
      setPlacing(null);
    }
  }

  function copyBundleMail(group: SupplierGroup) {
    const subject = `Inkoopopdracht (gebundeld) — ${group.supplier.name}`;
    const lines: string[] = [
      `Beste ${group.supplier.name},`,
      '',
      'Graag onderstaande bestelling:',
      '',
    ];
    for (const p of group.products) {
      lines.push(`  • ${p.productName} — ${p.total} stuks`);
    }
    lines.push('', 'Met vriendelijke groet,', '2BMade');
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">
            Inkoop-overzicht
          </h1>
          <p className="text-sm text-bmade-600 dark:text-bmade-400">
            Te bestellen inkoop per leverancier, gebundeld over projecten. Plaats een bestelronde om
            de bestelling vast te leggen.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-bmade-200 px-3 py-1.5 text-xs text-bmade-700 hover:border-accent-400 dark:border-bmade-700 dark:text-bmade-300"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
          Verversen
        </button>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-bmade-500">Laden...</p>
      ) : groups.length === 0 ? (
        <div className="rounded-xl border border-bmade-200 bg-white p-6 text-sm text-bmade-500 dark:border-bmade-800 dark:bg-bmade-900/40">
          Geen te bestellen inkoop. Geplaatste rondes staan vast en verdwijnen uit dit overzicht.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div
              key={group.supplier.id}
              className="rounded-xl border border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40"
            >
              <header className="flex items-center justify-between border-b border-bmade-200 px-4 py-3 dark:border-bmade-800">
                <div>
                  <h3 className="inline-flex items-center gap-2 text-base font-semibold text-bmade-950 dark:text-white">
                    <Layers className="h-4 w-4 text-accent-500" />
                    {group.supplier.name}
                  </h3>
                  <p className="text-xs text-bmade-500">
                    {group.products.length} producten · {group.orders.length}{' '}
                    {group.orders.length === 1 ? 'project' : 'projecten'} · totaal{' '}
                    <span className="font-semibold text-bmade-950 dark:text-white">
                      {formatEur(group.total)}
                    </span>{' '}
                    excl btw
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyBundleMail(group)}
                    className="inline-flex items-center gap-1 rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 hover:border-accent-400 dark:border-bmade-700 dark:text-bmade-300"
                  >
                    <Mail className="h-3 w-3" />
                    Mail
                  </button>
                  <button
                    type="button"
                    onClick={() => void placeRound(group)}
                    disabled={placing !== null}
                    className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
                  >
                    <PackageCheck className="h-3 w-3" />
                    {placing === group.supplier.id ? 'Plaatsen...' : 'Bestelronde plaatsen'}
                  </button>
                </div>
              </header>
              <ul className="divide-y divide-bmade-200 dark:divide-bmade-800">
                {group.products.map((p) => {
                  const open = expanded.has(group.supplier.id + p.productId);
                  return (
                    <li key={p.productId} className="px-4 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggle(group.supplier.id + p.productId)}
                        className="flex w-full items-center justify-between text-left"
                      >
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-bmade-950 dark:text-white">
                          {open ? (
                            <ChevronDown className="h-3.5 w-3.5 text-bmade-400" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-bmade-400" />
                          )}
                          {p.productName}
                        </span>
                        <span className="text-sm font-semibold text-bmade-800 dark:text-bmade-200">
                          {p.total} stuks
                        </span>
                      </button>
                      {open && (
                        <ul className="ml-5 mt-1.5 space-y-1 border-l border-bmade-200 pl-3 dark:border-bmade-800">
                          {p.herkomst.map((h, i) => (
                            <li
                              key={i}
                              className="flex items-center justify-between text-xs text-bmade-600 dark:text-bmade-400"
                            >
                              <span>{h.projectName}</span>
                              <span>{h.quantity} stuks</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <StockUsePicker
                        needed={p.total}
                        free={(stockMap.get(p.productId) ?? []).reduce((s, l) => s + l.free, 0)}
                        busy={reserving === p.productId}
                        onReserve={(qty) => void reserveStock(p.productId, qty)}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
